import React, {
  ChangeEvent,
  FC,
  memo,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { RadioGroup, FormControl, Grid } from '@material-ui/core';

import { FilterData, FilterOnChange, FilterValue } from '../filters.types';
import { FormControlLabel, Text, Count, Radio } from './radio.styles';

type Props = {
  name: string;
  data: FilterData[];
  value?: FilterValue;
  onChange: FilterOnChange;
};

type RenderFormControlLabel = (data: FilterData) => JSX.Element;

type OnChange = (e: ChangeEvent<HTMLInputElement>, value: string) => void;

type RenderLabel = (text: string, count: number | null) => JSX.Element;

type ConvertStringToNumber = (value: string) => string | number;

const renderLabel: RenderLabel = (text, count) => (
  <Grid container justifyContent="space-between" alignItems="center">
    <Text variant="body2" color="textPrimary">
      {text}
    </Text>
    {!!count && (
      <Count variant="body2" color="textPrimary">
        {count}
      </Count>
    )}
  </Grid>
);

const renderFormControlLabel: RenderFormControlLabel = ({
  text,
  value,
  count,
}) => (
  <Grid key={value} container item sm={6} alignItems="center">
    <FormControlLabel
      disabled={count === 0}
      value={value.toString()}
      control={<Radio />}
      label={renderLabel(text, count)}
    />
  </Grid>
);

const convertStringToNumber: ConvertStringToNumber = (value) => {
  const valueToNumber = parseFloat(value);
  const isValueNaN = Number.isNaN(valueToNumber);

  return isValueNaN ? value : valueToNumber;
};

export const RadioFields: FC<Props> = memo(
  ({ name, data, value = null, onChange }) => {
    const generateInitialValue = useMemo(
      () => (value === null ? null : value.toString()),
      [value]
    );
    const [state, setState] = useState<string | number | null>(
      generateInitialValue
    );

    useEffect(() => {
      setState(generateInitialValue);
    }, [generateInitialValue]);

    const handleChange: OnChange = (e, value) => {
      const customValue = convertStringToNumber(value);

      setState(customValue);
      onChange(name, customValue);
    };

    return (
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="radio-group"
          name={name}
          value={state}
          onChange={handleChange}
        >
          <Grid container spacing={1}>
            {data.map(renderFormControlLabel)}
          </Grid>
        </RadioGroup>
      </FormControl>
    );
  }
);
